import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, DOCUMENT, inject, InjectionToken, PLATFORM_ID, Service, signal } from '@angular/core';
import { SessionStore } from '@app/core/session/session-store';
import { CLOCK } from '@app/core/time/clock';
import { environment } from '@env/environment';
import { RealtimeTicketClient } from '@shared/api/identity/clients/realtime-ticket.client';
import type { RealtimeClientFrameDto } from '@shared/api/realtime/dtos/realtime-client-frame.dto';
import type { Subscription } from 'rxjs';
import { EMPTY, Observable, Subject } from 'rxjs';

import type { RealtimeConnectionState, RealtimeFeedItem } from './realtime-connection.types';
import { realtimeFrameFrom } from './realtime-frame.util';

export const REALTIME_ENDPOINT = new InjectionToken<string>('realtime.endpoint', { providedIn: 'root', factory: () => environment.urlRealtime });

export const REALTIME_RETRY_JITTER = new InjectionToken<() => number>('realtime.retry-jitter', { providedIn: 'root', factory: () => () => Math.random() });

export const REALTIME_SOCKET_FACTORY = new InjectionToken<(url: string) => WebSocket>('realtime.socket-factory', {
  providedIn: 'root',
  factory: () => (url: string) => new WebSocket(url),
});

export const FIRST_RETRY_MS = 1_000;
export const LONGEST_RETRY_MS = 30_000;
export const HANDSHAKE_TIMEOUT_MS = 10_000;
export const UNWATCHED_GRACE_MS = 15_000;

const RETRY_GROWTH_FACTOR = 2;
const GUARANTEED_WAIT_SHARE = 0.5;
const NORMAL_CLOSURE = 1000;

type ConnectionPhase = 'idle' | 'ticket' | 'opening' | 'authenticating' | 'live' | 'waiting';

@Service()
export class RealtimeConnectionService {
  private readonly realtimeTicketClient = inject(RealtimeTicketClient);
  private readonly sessionStore = inject(SessionStore);
  private readonly endpoint = inject(REALTIME_ENDPOINT);
  private readonly socketFactory = inject(REALTIME_SOCKET_FACTORY);
  private readonly jitter = inject(REALTIME_RETRY_JITTER);
  private readonly clock = inject(CLOCK);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly connectionState = signal<RealtimeConnectionState>({ status: 'idle' });
  private readonly feed = new Subject<RealtimeFeedItem>();
  private readonly interestByScope = new Map<string, number>();
  private phase: ConnectionPhase = 'idle';
  private socket: WebSocket | null = null;
  private ticketRequest: Subscription | null = null;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private handshakeTimer?: ReturnType<typeof setTimeout>;
  private graceTimer?: ReturnType<typeof setTimeout>;
  private failedAttempts = 0;
  private droppedAt: Date | null = null;

  readonly state = this.connectionState.asReadonly();

  init(): void {
    const browserWindow = this.document.defaultView;

    if (!isPlatformBrowser(this.platformId) || !browserWindow) {
      return;
    }

    const suspend = () => this.disconnect();
    const resume = (event: PageTransitionEvent) => this.reconnectFromBackForwardCache(event);
    const retryOnline = () => this.retryNow();

    browserWindow.addEventListener('pagehide', suspend);
    browserWindow.addEventListener('pageshow', resume);
    browserWindow.addEventListener('online', retryOnline);

    this.destroyRef.onDestroy(() => {
      browserWindow.removeEventListener('pagehide', suspend);
      browserWindow.removeEventListener('pageshow', resume);
      browserWindow.removeEventListener('online', retryOnline);
      this.disconnect();
    });
  }

  watch(scope: string): Observable<RealtimeFeedItem> {
    if (!isPlatformBrowser(this.platformId)) {
      return EMPTY;
    }

    return new Observable<RealtimeFeedItem>(subscriber => {
      const delivery = this.feed.subscribe(subscriber);
      this.addInterest(scope);

      return () => {
        delivery.unsubscribe();
        this.removeInterest(scope);
      };
    });
  }

  retryNow(): void {
    if (this.phase !== 'waiting') {
      return;
    }

    clearTimeout(this.retryTimer);
    this.connect();
  }

  clear(): void {
    this.disconnect();
  }

  private addInterest(scope: string): void {
    const interest = this.interestByScope.get(scope) ?? 0;
    this.interestByScope.set(scope, interest + 1);
    clearTimeout(this.graceTimer);

    if (interest === 0 && this.phase === 'live') {
      this.send({ type: 'watch', scope });
    }

    if (this.phase === 'idle') {
      this.connect();
    }
  }

  private removeInterest(scope: string): void {
    const remaining = (this.interestByScope.get(scope) ?? 0) - 1;

    if (remaining > 0) {
      this.interestByScope.set(scope, remaining);

      return;
    }

    this.interestByScope.delete(scope);

    if (this.phase === 'live') {
      this.send({ type: 'unwatch', scope });
    }

    if (this.interestByScope.size === 0) {
      this.graceTimer = setTimeout(() => this.disconnect(), UNWATCHED_GRACE_MS);
    }
  }

  private reconnectFromBackForwardCache(event: PageTransitionEvent): void {
    if (event.persisted && this.phase === 'idle' && this.interestByScope.size > 0) {
      this.connect();
    }
  }

  private disconnect(): void {
    this.ticketRequest?.unsubscribe();
    clearTimeout(this.retryTimer);
    clearTimeout(this.graceTimer);
    this.closeSocket();
    this.phase = 'idle';
    this.failedAttempts = 0;
    this.droppedAt = null;
    this.connectionState.set({ status: 'idle' });
  }

  private connect(): void {
    this.phase = 'ticket';
    this.connectionState.set(this.droppedAt === null ? { status: 'connecting' } : { status: 'reconnecting', since: this.droppedAt });
    this.ticketRequest = this.realtimeTicketClient.issueTicket().subscribe({
      next: response => this.open(response.ticket),
      error: () => this.recoverFromTicketFailure(),
    });
  }

  private recoverFromTicketFailure(): void {
    if (!this.sessionStore.isSignedIn()) {
      this.clear();

      return;
    }

    this.waitAndRetry();
  }

  private open(ticket: string): void {
    this.phase = 'opening';
    const socket = this.socketFactory(this.endpoint);
    this.socket = socket;
    socket.onopen = () => this.authenticate(ticket);
    socket.onmessage = message => this.receive(message.data);
    socket.onclose = () => this.recoverFromDrop();
    this.handshakeTimer = setTimeout(() => this.abandonHandshake(), HANDSHAKE_TIMEOUT_MS);
  }

  private authenticate(ticket: string): void {
    this.phase = 'authenticating';
    this.send({ type: 'auth', ticket });
  }

  private receive(data: unknown): void {
    const frame = realtimeFrameFrom(data);

    if (frame?.type === 'ready') {
      this.becomeLive();

      return;
    }

    if (frame?.type === 'event' && frame.event.idBusiness === this.sessionStore.getSession()?.idBusiness) {
      this.feed.next({ type: 'event', event: frame.event });
    }
  }

  private becomeLive(): void {
    clearTimeout(this.handshakeTimer);
    this.phase = 'live';
    this.failedAttempts = 0;
    this.droppedAt = null;

    for (const scope of this.interestByScope.keys()) {
      this.send({ type: 'watch', scope });
    }

    this.connectionState.set({ status: 'live' });
    this.feed.next({ type: 'resync' });
  }

  private abandonHandshake(): void {
    this.closeSocket();
    this.waitAndRetry();
  }

  private recoverFromDrop(): void {
    this.forgetSocket();
    this.waitAndRetry();
  }

  private waitAndRetry(): void {
    this.droppedAt ??= this.clock();
    this.phase = 'waiting';
    this.connectionState.set({ status: 'reconnecting', since: this.droppedAt });
    this.retryTimer = setTimeout(() => this.connect(), this.nextWaitMs());
    this.failedAttempts++;
  }

  private nextWaitMs(): number {
    const baseMs = Math.min(LONGEST_RETRY_MS, FIRST_RETRY_MS * RETRY_GROWTH_FACTOR ** this.failedAttempts);

    return baseMs * (GUARANTEED_WAIT_SHARE + (1 - GUARANTEED_WAIT_SHARE) * this.jitter());
  }

  private closeSocket(): void {
    const socket = this.socket;
    this.forgetSocket();
    socket?.close(NORMAL_CLOSURE);
  }

  private forgetSocket(): void {
    clearTimeout(this.handshakeTimer);

    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onclose = null;
    }

    this.socket = null;
  }

  private send(frame: RealtimeClientFrameDto): void {
    this.socket?.send(JSON.stringify(frame));
  }
}
